import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import DynamicPlotlyChart from './DynamicPlotlyChart';
import TypingText from './TypingText';
import DownloadCSV from './DownloadCSV';
import ThumbsUpIcon from '../icons/ThumbsUpIcon.svg';
import ThumbsDownIcon from '../icons/ThumbsDownIcon.svg';
import ThumbsUpIconFilled from '../icons/Like_Active.svg';
import ThumbsDownIconFilled from '../icons/Dislike_Active.svg';
import CopyIcon from '../icons/Copy.svg';
import { useSelector } from 'store/hooks';
import ReactMarkdown from 'react-markdown';

const DynamicMessageCard = ({ isMaximized, onFeedback }) => {
  const maxWidth = isMaximized ? 560 : 308;

  const { conversationList } = useSelector((state) => state.ai);

  const handleCopy = async (index) => {
    const database = conversationList[index - 1];

    if (!database) {
      console.warn('No data found at the given index.');
      return;
    }

    try {
      const { type, msg } = database;

      let textToCopy: string;

      if (typeof msg === 'string') {
        textToCopy = msg;
      } else if (typeof msg === 'object' && msg !== null) {
        // Convert the object to a string representation, e.g., JSON string
        textToCopy = JSON.stringify(msg);
      } else {
        // Handle other unexpected types, perhaps by setting an empty string or throwing an error
        textToCopy = '';
      }

      if (type === 'explaination' || type === 'database') {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(textToCopy);
          console.log('Copied to clipboard.');
        } else {
          // Fallback for unsupported environments
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          console.log('Copied using fallback method.');
        }
      } else {
        console.log('This content type cannot be copied.');
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <>
      {conversationList?.map((item: any, index: number) => (
        <Box key={item?.id}>
          {/* QUERY */}
          {item?.type === 'query' && (
            <Box display="flex" justifyContent="flex-end">
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: '#ECF4FF',
                  maxWidth,
                  wordBreak: 'break-word',
                }}
              >
                {item?.msg}
              </Paper>
            </Box>
          )}

          {/* GENERAL / DATABASE */}
          {(item?.type === 'general' || item?.type === 'database') && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#F6F6F6',
                maxWidth,
                wordBreak: 'break-word',
              }}
            >
              {conversationList?.length - 1 === index ? <TypingText text={item?.msg} speed={20} /> : item?.msg}
            </Paper>
          )}

          {item?.type === 'response' &&
            (() => {
              const severity = 'error';

              return (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: '#F6F6F6',
                    maxWidth,
                    wordBreak: 'break-word',
                  }}
                >
                  <Stack spacing={1}>
                    <Alert icon={false} severity={severity} sx={{ marginBottom: 1 }}>
                      {item?.msg}
                    </Alert>
                  </Stack>
                </Paper>
              );
            })()}

          {/* Confidence Level */}
          {item?.type === 'confidencelevel' &&
            (() => {
              let severity: 'success' | 'info' | 'warning' | 'error' = 'info';

              if (typeof item?.msg === 'string') {
                const msgLower = item.msg.toLowerCase();
                if (msgLower.includes('medium')) {
                  severity = 'error';
                } else if (msgLower.includes('high')) {
                  severity = 'warning';
                } else if (msgLower.includes('low')) {
                  severity = 'success';
                }
              }

              return (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 1,
                    borderRadius: 2,
                    backgroundColor: '#F6F6F6',
                    maxWidth,
                    wordBreak: 'break-word',
                  }}
                >
                  <Stack spacing={1}>
                    <Alert icon={false} severity={severity} sx={{ marginBottom: 1 }}>
                      {`Confidence Level: ${item?.msg}`}
                    </Alert>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Copy response">
                      <IconButton onClick={() => handleCopy(index)} aria-label="Copy response">
                        <img src={CopyIcon} alt="Copy" />
                        {/* Or replace with <ContentCopyIcon /> */}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Like response">
                      <IconButton
                        disabled={item.feedback === 'no'}
                        onClick={() => onFeedback(item.log_id, item.feedback === 'yes' ? null : 'yes')}
                        aria-label="Like response"
                        sx={{
                          bgcolor: item.feedback === 'yes' ? 'green.100' : 'transparent',
                          color: item.feedback === 'yes' ? 'green.600' : 'text.secondary',
                          pointerEvents: item.feedback === null ? 'auto' : 'none', // Optional: prevent interaction
                          opacity: item.feedback === 'yes' || item.feedback === null ? 1 : 0.2,
                          '&:hover': {
                            bgcolor: item.feedback === 'yes' ? 'green.200' : 'grey.100',
                          },
                        }}
                      >
                        <img src={item.feedback === 'yes' ? ThumbsUpIconFilled : ThumbsUpIcon} alt="Thumbs up" />
                        {/* Or use MUI icon components instead */}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Dislike response">
                      <IconButton
                        disabled={item.feedback === 'yes'}
                        onClick={() => onFeedback(item.log_id, item.feedback === 'no' ? null : 'no')}
                        aria-label="Dislike response"
                        sx={{
                          bgcolor: item.feedback === 'no' ? 'red.100' : 'transparent',
                          color: item.feedback === 'no' ? 'red.600' : 'text.secondary',
                          pointerEvents: item.feedback === null ? 'auto' : 'none', // Optional: prevent interaction
                          opacity: item.feedback === 'no' || item.feedback === null ? 1 : 0.2,
                          '&:hover': {
                            bgcolor: item.feedback === 'no' ? 'red.200' : 'grey.100',
                          },
                        }}
                      >
                        <img src={item.feedback === 'no' ? ThumbsDownIconFilled : ThumbsDownIcon} alt="Thumbs down" />
                        {/* Or use MUI icon components instead */}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              );
            })()}

          {/* EXPLANATION */}
          {item?.type === 'explaination' && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#F6F6F6',
                maxWidth,
                wordBreak: 'break-word',
              }}
            >
              <Stack spacing={1}>
                {item?.msg?.split('\n').map((line: any, lineIndex: number) => (
                  <ReactMarkdown>{line}</ReactMarkdown>
                ))}
              </Stack>
            </Paper>
          )}

          {/* GRAPH */}
          {item?.type === 'graph' && (
            <DynamicPlotlyChart
              chartData={item?.msg}
              width={isMaximized ? '600' : '350'}
              height={isMaximized ? '600' : '350'}
            />
          )}

          {/* REPORT */}
          {item?.type === 'report' && item?.msg && typeof item.msg === 'object' && Object.keys(item.msg).length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#F6F6F6',
                maxWidth,
              }}
            >
              <Typography>The requested report has been generated successfully.</Typography>

              <Box
                mt={3}
                sx={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  boxShadow: 1,
                  overflow: 'hidden',
                  maxWidth,
                }}
              >
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table stickyHeader size="small">
                    <TableHead sx={{ backgroundColor: '#f3f4f6' }}>
                      <TableRow>
                        {Object.keys(item?.msg).map((key) => (
                          <TableCell key={key}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.keys(item?.msg[Object.keys(item?.msg)[0]]).map((rowKey) => (
                        <TableRow key={rowKey}>
                          {Object.keys(item?.msg).map((colKey) => (
                            <TableCell key={colKey}>{item?.msg[colKey][rowKey]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box mt={1}>
                <DownloadCSV data={item?.msg} />
              </Box>
            </Paper>
          )}
        </Box>
      ))}
    </>
  );
};

export default DynamicMessageCard;
